'use client';

import {
	type TAppExperienceContent,
	type TBusinessCardExperienceContent,
	type TCouponExperienceContent,
	type TMenuExperienceContent,
	type TPdfExperienceContent,
	type TQrExperienceContent,
	type TQrExperiencePublicDto,
	type TQrExperienceType,
	type TSocialLinksExperienceContent,
	type TVCardInput,
} from '@shared/schemas';
import { PublicExperiencePage } from '@/components/qr-experience/PublicExperiencePage';
import {
	Phone,
	Mail,
	Globe,
	MapPin,
	MessageSquare,
	UserPlus,
	FileText,
	Lock,
	RefreshCw,
	ExternalLink,
	MessageCircle,
	Wifi,
	CalendarDays,
	Clock,
} from 'lucide-react';

interface Props {
	experienceContent: Partial<TQrExperienceContent>;
	experienceType: TQrExperienceType | 'vcard' | 'website' | 'whatsapp' | 'wifi' | 'event';
	vcardData?: Partial<TVCardInput>;
	websiteUrl?: string;
	whatsappData?: { phone?: string; message?: string };
	wifiData?: { ssid?: string; encryption?: string };
	eventData?: { title?: string; startDate?: string; location?: string };
}

function VCardPhoneScreen({ vcard }: { vcard?: Partial<TVCardInput> }) {
	const hasUserData = Boolean(
		vcard &&
		Object.entries(vcard).some(
			([k, v]) => k !== 'isDynamic' && typeof v === 'string' && v.trim().length > 0,
		),
	);

	const firstName = hasUserData ? vcard?.firstName?.trim() || '' : 'Alex';
	const lastName = hasUserData ? vcard?.lastName?.trim() || '' : 'Morgan';
	const fullName =
		[firstName, lastName].filter(Boolean).join(' ') ||
		(hasUserData ? 'Contact Name' : 'Alex Morgan');

	let initials = '';
	if (firstName) initials += firstName.charAt(0).toUpperCase();
	if (lastName) initials += lastName.charAt(0).toUpperCase();
	if (!initials) initials = 'AM';

	const company = hasUserData ? vcard?.company?.trim() || '' : 'TechCorp Global';
	const job = hasUserData ? vcard?.job?.trim() || '' : 'VP of Product';
	const phoneMobile = hasUserData ? vcard?.phoneMobile?.trim() || '' : '+1 (555) 234-5678';
	const phoneBusiness = hasUserData ? vcard?.phoneBusiness?.trim() || '' : '';
	const emailBusiness = hasUserData
		? vcard?.emailBusiness?.trim() || ''
		: 'alex.morgan@techcorp.io';
	const emailPrivate = hasUserData ? vcard?.emailPrivate?.trim() || '' : '';
	const website = hasUserData ? vcard?.website?.trim() || '' : 'https://techcorp.io';

	const addressParts = [
		vcard?.streetBusiness?.trim(),
		vcard?.cityBusiness?.trim(),
		vcard?.stateBusiness?.trim(),
		vcard?.zipBusiness?.trim(),
		vcard?.countryBusiness?.trim(),
	].filter(Boolean);

	const address = hasUserData
		? addressParts.join(', ')
		: '500 Howard Street, San Francisco, CA 94105';

	const note = hasUserData ? vcard?.note?.trim() || '' : 'Met at Tech Summit 2026';
	const photo = vcard?.photo?.trim();

	const handleDownload = () => {
		const fullAddress = addressParts.join(', ') || address;
		const photoLine = photo
			? photo.startsWith('data:image/')
				? `PHOTO;ENCODING=b;TYPE=JPEG:${photo.replace(/^data:image\/[a-zA-Z0-9+]+;base64,/, '')}`
				: `PHOTO;VALUE=uri:${photo}`
			: undefined;

		const lines = [
			'BEGIN:VCARD',
			'VERSION:3.0',
			`N:${lastName || ''};${firstName || ''};;;`,
			`FN:${fullName}`,
			company && `ORG:${company}`,
			job && `TITLE:${job}`,
			photoLine,
			phoneMobile && `TEL;TYPE=CELL:${phoneMobile}`,
			phoneBusiness && `TEL;TYPE=WORK:${phoneBusiness}`,
			emailBusiness && `EMAIL;TYPE=WORK:${emailBusiness}`,
			emailPrivate && `EMAIL;TYPE=HOME:${emailPrivate}`,
			fullAddress && `ADR;TYPE=WORK:;;${fullAddress}`,
			website && `URL:${website}`,
			note && `NOTE:${note}`,
			'END:VCARD',
		].filter(Boolean);

		const blob = new Blob([lines.join('\r\n')], { type: 'text/vcard;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${fullName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'contact'}.vcf`;
		link.click();
		window.setTimeout(() => URL.revokeObjectURL(url), 1000);
	};

	return (
		<div className="w-full bg-slate-50 relative pb-10 select-text">
			{/* Top Hero Banner */}
			<div className="relative h-28 bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-800 flex flex-col items-center justify-start pt-5 text-white overflow-hidden">
				<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none" />
				<div className="absolute -top-6 -right-6 size-24 rounded-full bg-blue-400 blur-xl opacity-30 pointer-events-none" />
				<div className="absolute -bottom-px left-0 right-0 h-4 bg-slate-50 [clip-path:ellipse(65%_100%_at_50%_100%)]" />
			</div>

			{/* Profile Info Card */}
			<div className="px-3.5 -mt-12 relative z-10 text-center">
				<div className="bg-white rounded-2xl p-3.5 shadow-md border border-slate-100 mx-auto inline-block w-full">
					{photo ? (
						<div className="size-16 rounded-full mx-auto overflow-hidden shadow-xs mb-2 border-2 border-white ring-4 ring-indigo-50/70 bg-slate-100 flex items-center justify-center">
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src={photo} alt={fullName} className="size-full object-cover rounded-full" />
						</div>
					) : (
						<div className="size-16 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 mx-auto flex items-center justify-center text-xl font-bold text-white shadow-xs mb-2 border-2 border-white ring-4 ring-indigo-50/70">
							{initials}
						</div>
					)}
					<h2 className="text-sm font-bold tracking-tight text-slate-900 leading-tight">
						{fullName}
					</h2>
					{(job || company) && (
						<p className="mt-0.5 text-[10.5px] font-medium text-slate-500 line-clamp-1">
							{[job, company].filter(Boolean).join(' • ')}
						</p>
					)}

					{/* Quick Action Buttons */}
					<div className="mt-3 grid grid-cols-4 gap-1.5 pt-2 border-t border-slate-100">
						<a
							href={phoneMobile ? `tel:${phoneMobile}` : undefined}
							className="flex flex-col items-center gap-1 p-1.5 rounded-xl bg-slate-50 hover:bg-indigo-50 text-indigo-600 transition group"
							title="Call"
						>
							<div className="size-7 rounded-lg bg-indigo-100/70 group-hover:bg-indigo-600 group-hover:text-white flex items-center justify-center transition">
								<Phone className="size-3.5" />
							</div>
							<span className="text-[9px] font-semibold text-slate-600">Call</span>
						</a>
						<a
							href={phoneMobile ? `sms:${phoneMobile}` : undefined}
							className="flex flex-col items-center gap-1 p-1.5 rounded-xl bg-slate-50 hover:bg-emerald-50 text-emerald-600 transition group"
							title="Message"
						>
							<div className="size-7 rounded-lg bg-emerald-100/70 group-hover:bg-emerald-600 group-hover:text-white flex items-center justify-center transition">
								<MessageSquare className="size-3.5" />
							</div>
							<span className="text-[9px] font-semibold text-slate-600">Text</span>
						</a>
						<a
							href={emailBusiness ? `mailto:${emailBusiness}` : undefined}
							className="flex flex-col items-center gap-1 p-1.5 rounded-xl bg-slate-50 hover:bg-amber-50 text-amber-600 transition group"
							title="Email"
						>
							<div className="size-7 rounded-lg bg-amber-100/70 group-hover:bg-amber-600 group-hover:text-white flex items-center justify-center transition">
								<Mail className="size-3.5" />
							</div>
							<span className="text-[9px] font-semibold text-slate-600">Email</span>
						</a>
						<a
							href={website || undefined}
							target="_blank"
							rel="noopener noreferrer"
							className="flex flex-col items-center gap-1 p-1.5 rounded-xl bg-slate-50 hover:bg-sky-50 text-sky-600 transition group"
							title="Website"
						>
							<div className="size-7 rounded-lg bg-sky-100/70 group-hover:bg-sky-600 group-hover:text-white flex items-center justify-center transition">
								<Globe className="size-3.5" />
							</div>
							<span className="text-[9px] font-semibold text-slate-600">Web</span>
						</a>
					</div>

					{/* Primary "Add to Contacts" Button */}
					<button
						type="button"
						onClick={handleDownload}
						className="mt-2.5 w-full py-2 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-xs flex items-center justify-center gap-1.5 shadow-xs transition cursor-pointer"
					>
						<UserPlus className="size-3.5" />
						Save to Contacts
					</button>
				</div>
			</div>

			{/* Contact Details Stack */}
			<div className="px-3.5 mt-2.5 space-y-2 pb-6">
				{/* Phone Numbers */}
				{(phoneMobile || phoneBusiness) && (
					<div className="bg-white rounded-xl border border-slate-200/80 p-2.5 shadow-2xs space-y-2">
						<div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-semibold uppercase tracking-wider px-0.5">
							<Phone className="size-2.5" />
							<span>Phone</span>
						</div>
						{phoneMobile && (
							<div className="flex items-center justify-between px-0.5">
								<div>
									<span className="text-[9.5px] text-slate-400 block leading-tight">Mobile</span>
									<span className="text-[11px] font-semibold text-slate-900">{phoneMobile}</span>
								</div>
								<a
									href={`tel:${phoneMobile}`}
									className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50"
								>
									<Phone className="size-3" />
								</a>
							</div>
						)}
						{phoneBusiness && (
							<div className="flex items-center justify-between px-0.5 pt-1.5 border-t border-slate-100">
								<div>
									<span className="text-[9.5px] text-slate-400 block leading-tight">Work</span>
									<span className="text-[11px] font-semibold text-slate-900">{phoneBusiness}</span>
								</div>
								<a
									href={`tel:${phoneBusiness}`}
									className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50"
								>
									<Phone className="size-3" />
								</a>
							</div>
						)}
					</div>
				)}

				{/* Emails */}
				{(emailBusiness || emailPrivate) && (
					<div className="bg-white rounded-xl border border-slate-200/80 p-2.5 shadow-2xs space-y-2">
						<div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-semibold uppercase tracking-wider px-0.5">
							<Mail className="size-2.5" />
							<span>Email</span>
						</div>
						{emailBusiness && (
							<div className="flex items-center justify-between px-0.5">
								<div className="min-w-0 flex-1 pr-2">
									<span className="text-[9.5px] text-slate-400 block leading-tight">Work</span>
									<span className="text-[11px] font-semibold text-slate-900 truncate block">
										{emailBusiness}
									</span>
								</div>
								<a
									href={`mailto:${emailBusiness}`}
									className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 shrink-0"
								>
									<Mail className="size-3" />
								</a>
							</div>
						)}
						{emailPrivate && (
							<div className="flex items-center justify-between px-0.5 pt-1.5 border-t border-slate-100">
								<div className="min-w-0 flex-1 pr-2">
									<span className="text-[9.5px] text-slate-400 block leading-tight">Personal</span>
									<span className="text-[11px] font-semibold text-slate-900 truncate block">
										{emailPrivate}
									</span>
								</div>
								<a
									href={`mailto:${emailPrivate}`}
									className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 shrink-0"
								>
									<Mail className="size-3" />
								</a>
							</div>
						)}
					</div>
				)}

				{/* Address */}
				{address && (
					<div className="bg-white rounded-xl border border-slate-200/80 p-2.5 shadow-2xs">
						<div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-semibold uppercase tracking-wider px-0.5 pb-1">
							<MapPin className="size-2.5 text-rose-500" />
							<span>Address</span>
						</div>
						<p className="text-[10.5px] font-semibold text-slate-800 leading-snug px-0.5">
							{address}
						</p>
					</div>
				)}

				{/* Website */}
				{website && (
					<div className="bg-white rounded-xl border border-slate-200/80 p-2.5 shadow-2xs">
						<div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-semibold uppercase tracking-wider px-0.5 pb-1">
							<Globe className="size-2.5 text-sky-500" />
							<span>Website</span>
						</div>
						<a
							href={website}
							target="_blank"
							rel="noopener noreferrer"
							className="text-[10.5px] font-semibold text-indigo-600 hover:underline truncate block px-0.5"
						>
							{website.replace(/^https?:\/\//, '')}
						</a>
					</div>
				)}

				{/* Note */}
				{note && (
					<div className="bg-white rounded-xl border border-slate-200/80 p-2.5 shadow-2xs">
						<div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-semibold uppercase tracking-wider px-0.5 pb-1">
							<FileText className="size-2.5 text-amber-500" />
							<span>Note</span>
						</div>
						<p className="text-[10.5px] text-slate-600 leading-relaxed px-0.5 italic">
							&ldquo;{note}&rdquo;
						</p>
					</div>
				)}
			</div>
		</div>
	);
}

function WebsitePhoneScreen({ url }: { url?: string }) {
	const displayUrl = url?.trim() || 'https://qrcode.ekutulimited.com';
	return (
		<div className="flex flex-col h-full bg-slate-100 text-slate-800">
			{/* Safari Address Bar */}
			<div className="bg-slate-200/90 backdrop-blur-md px-3 py-2 border-b border-slate-300/70 flex items-center justify-between text-[11px] text-slate-600">
				<span className="font-mono text-xs">AA</span>
				<div className="flex-1 mx-2 bg-white/90 rounded-lg px-2.5 py-1 flex items-center justify-center gap-1.5 shadow-2xs truncate">
					<Lock className="size-2.5 text-slate-500 shrink-0" />
					<span className="truncate max-w-[170px] text-[10px] font-medium">
						{displayUrl.replace(/^https?:\/\//, '')}
					</span>
				</div>
				<RefreshCw className="size-3 text-slate-500" />
			</div>

			{/* Webpage Content */}
			<div className="flex-1 p-5 flex flex-col items-center justify-center text-center bg-gradient-to-b from-white to-slate-50">
				<div className="size-16 rounded-2xl bg-sky-50 border border-sky-200/80 text-sky-600 flex items-center justify-center shadow-sm mb-4">
					<Globe className="size-8" />
				</div>
				<h3 className="text-base font-bold text-slate-900 leading-snug mb-1.5">
					Instant Web Redirection
				</h3>
				<p className="text-xs text-slate-500 max-w-[210px] leading-relaxed mb-6">
					Visitors scanning this QR code are immediately redirected to your webpage.
				</p>
				<div className="w-full max-w-[220px] py-2.5 px-4 rounded-xl bg-sky-600 text-white font-semibold text-xs shadow-sm flex items-center justify-center gap-1.5 cursor-pointer">
					<span>Open Website</span>
					<ExternalLink className="size-3.5" />
				</div>
				<span className="mt-4 text-[10px] text-emerald-600 font-medium flex items-center gap-1">
					<span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
					Dynamic URL • Editable Anytime
				</span>
			</div>
		</div>
	);
}

function WhatsAppPhoneScreen({ data }: { data?: { phone?: string; message?: string } }) {
	const phone = data?.phone?.trim() || '+1 (555) 019-2834';
	const message =
		data?.message?.trim() || 'Hello! I scanned your QR code and would like to learn more.';
	return (
		<div className="flex flex-col h-full bg-[#efeae2] text-slate-800">
			{/* WhatsApp Header */}
			<div className="bg-[#075e54] text-white px-3 py-2.5 flex items-center gap-2.5 shadow-sm">
				<div className="size-8 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center">
					WA
				</div>
				<div className="flex-1 min-w-0">
					<p className="text-xs font-semibold truncate leading-tight">{phone}</p>
					<p className="text-[10px] text-emerald-200 leading-tight">online</p>
				</div>
				<Phone className="size-3.5 text-white/90" />
			</div>

			{/* Chat Area */}
			<div className="flex-1 p-3.5 flex flex-col justify-end gap-3">
				<div className="self-center bg-amber-100/90 text-amber-900 text-[9.5px] px-2.5 py-1 rounded-md shadow-2xs text-center max-w-[230px]">
					🔒 Messages and calls are end-to-end encrypted.
				</div>

				<div className="self-end bg-[#d9fdd3] text-slate-900 rounded-xl rounded-tr-xs p-2.5 max-w-[220px] shadow-2xs text-xs leading-relaxed">
					<p>{message}</p>
					<div className="text-[9px] text-slate-500 text-right mt-1 flex items-center justify-end gap-0.5">
						<span>9:41 AM</span>
						<span className="text-[#53bdeb]">✓✓</span>
					</div>
				</div>

				<div className="mt-2 pt-2 border-t border-slate-300/60">
					<div className="w-full py-2.5 rounded-xl bg-[#25d366] text-white font-bold text-xs shadow-sm flex items-center justify-center gap-1.5 cursor-pointer">
						<MessageCircle className="size-4" />
						<span>Chat on WhatsApp</span>
					</div>
				</div>
			</div>
		</div>
	);
}

function WifiPhoneScreen({ data }: { data?: { ssid?: string; encryption?: string } }) {
	const ssid = data?.ssid?.trim() || 'Guest_WiFi_Fast';
	const encryption = data?.encryption || 'WPA2/WPA3';
	return (
		<div className="flex flex-col h-full bg-slate-900 text-white p-6 justify-center items-center text-center">
			<div className="size-20 rounded-3xl bg-cyan-500/15 border border-cyan-400/30 text-cyan-400 flex items-center justify-center mb-5 shadow-lg shadow-cyan-500/20">
				<Wifi className="size-10" />
			</div>
			<span className="px-3 py-1 rounded-full bg-cyan-950/80 border border-cyan-800/80 text-cyan-300 text-[10px] font-semibold mb-2">
				Wi-Fi Quick Connect
			</span>
			<h3 className="text-lg font-bold text-white tracking-tight mb-1">{ssid}</h3>
			<p className="text-xs text-slate-400 mb-6 flex items-center gap-1.5">
				<Lock className="size-3 text-slate-400" />
				<span>{encryption} Security</span>
			</p>
			<div className="w-full max-w-[220px] py-3 rounded-xl bg-cyan-500 text-slate-950 font-bold text-xs shadow-md shadow-cyan-500/30 transition-all cursor-pointer">
				Join This Network
			</div>
			<p className="text-[10px] text-slate-500 mt-4 max-w-[200px] leading-relaxed">
				Zero typing required. Scanners tap to instantly connect to Wi-Fi.
			</p>
		</div>
	);
}

function EventPhoneScreen({
	data,
}: {
	data?: { title?: string; startDate?: string; location?: string };
}) {
	const title = data?.title?.trim() || 'Tech Summit & Product Launch';
	const location = data?.location?.trim() || 'Grand Ballroom, Silicon Center';
	return (
		<div className="flex flex-col h-full bg-slate-50 text-slate-900 p-5 justify-between">
			<div className="flex flex-col items-center text-center pt-4">
				<div className="size-18 rounded-2xl bg-white border border-purple-200/80 shadow-sm overflow-hidden mb-4 flex flex-col items-center">
					<div className="w-full bg-purple-600 text-white text-[10px] font-bold py-0.5 uppercase tracking-wider">
						OCTOBER
					</div>
					<div className="flex-1 flex items-center justify-center text-2xl font-black text-slate-900">
						28
					</div>
				</div>
				<span className="px-2.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-semibold mb-2">
					Calendar Event
				</span>
				<h3 className="text-base font-bold text-slate-900 leading-snug mb-2">{title}</h3>
				<p className="text-xs text-purple-600 font-semibold mb-1 flex items-center gap-1">
					<Clock className="size-3" />
					<span>Wednesday, 10:00 AM – 4:00 PM</span>
				</p>
				<p className="text-[11px] text-slate-500 flex items-center gap-1 max-w-[220px] truncate">
					<MapPin className="size-3 text-slate-400 shrink-0" />
					<span className="truncate">{location}</span>
				</p>
			</div>

			<div className="pb-3">
				<div className="w-full py-3 rounded-xl bg-purple-600 text-white font-bold text-xs shadow-md shadow-purple-600/20 transition-all flex items-center justify-center gap-1.5 cursor-pointer">
					<CalendarDays className="size-4" />
					<span>Add to Calendar</span>
				</div>
			</div>
		</div>
	);
}

export function ExperiencePhonePreview({
	experienceContent,
	experienceType,
	vcardData,
	websiteUrl,
	whatsappData,
	wifiData,
	eventData,
}: Props) {
	const socialContent = experienceContent as Partial<TSocialLinksExperienceContent>;
	const businessContent = experienceContent as Partial<TBusinessCardExperienceContent>;
	const menuContent = experienceContent as Partial<TMenuExperienceContent>;
	const pdfContent = experienceContent as Partial<TPdfExperienceContent>;
	const couponContent = experienceContent as Partial<TCouponExperienceContent>;
	const appContent = experienceContent as Partial<TAppExperienceContent>;

	const hasUserBusinessData = Boolean(
		businessContent.fullName ||
		businessContent.company ||
		businessContent.role ||
		businessContent.tagline ||
		businessContent.address ||
		businessContent.phone ||
		businessContent.email ||
		businessContent.whatsApp ||
		businessContent.website ||
		businessContent.about ||
		businessContent.bio ||
		businessContent.logoUrl,
	);

	const hasUserSocialData = Boolean(
		socialContent.title ||
		socialContent.subtitle ||
		(socialContent.links && socialContent.links.length > 0),
	);

	let liveExperience: TQrExperiencePublicDto | undefined;

	if (experienceType !== 'vcard') {
		let liveContent: TQrExperienceContent;

		if (experienceType === 'social-links') {
			liveContent = {
				type: 'social-links',
				title: hasUserSocialData ? (socialContent.title ?? '') : 'Acme Studio',
				subtitle: hasUserSocialData
					? (socialContent.subtitle ?? '')
					: 'Connect with us across our official channels',
				links:
					socialContent.links && socialContent.links.length > 0
						? socialContent.links
						: hasUserSocialData
							? []
							: [
									{ platform: 'website', url: 'https://example.com', label: 'Official Website' },
									{ platform: 'linkedin', url: 'https://linkedin.com', label: 'LinkedIn' },
									{ platform: 'instagram', url: 'https://instagram.com', label: 'Instagram' },
								],
				headerColor: socialContent.headerColor || '#1d4ed8',
				accentColor: socialContent.accentColor || '#1d4ed8',
			};
		} else if (experienceType === 'menu') {
			liveContent = {
				type: 'menu',
				restaurantName: menuContent.restaurantName || 'Bella Trattoria',
				tagline: menuContent.tagline || 'Artisanal Italian Kitchen',
				description:
					menuContent.description ||
					'Traditional recipes made fresh daily with hand-picked organic ingredients.',
				currency: menuContent.currency || '$',
				phone: menuContent.phone,
				whatsApp: menuContent.whatsApp,
				website: menuContent.website,
				address: menuContent.address || '456 Little Italy Way, New York, NY',
				logoUrl: menuContent.logoUrl,
				categories:
					menuContent.categories && menuContent.categories.length > 0
						? menuContent.categories
						: [
								{
									id: 'cat-1',
									name: 'Starters & Antipasti',
									description: 'Authentic Italian small plates',
									items: [
										{
											id: 'm-1',
											name: 'Bruschetta al Pomodoro',
											description: 'Grilled country bread, garlic, ripe tomatoes, basil, EVOO',
											price: '9.50',
											dietary: ['vegetarian', 'vegan'],
											isAvailable: true,
										},
										{
											id: 'm-2',
											name: 'Crispy Calamari',
											description: 'Served with garlic herb aioli and charred lemon',
											price: '14.00',
											dietary: ['chef-special'],
											isAvailable: true,
										},
									],
								},
								{
									id: 'cat-2',
									name: 'Main Courses',
									description: 'Handmade pasta & wood-fired pizza',
									items: [
										{
											id: 'm-3',
											name: 'Truffle Tagliatelle',
											description: 'Parmesan cream, fresh black truffle shavings, wild mushrooms',
											price: '22.50',
											dietary: ['vegetarian', 'chef-special'],
											isAvailable: true,
										},
									],
								},
							],
				headerColor: menuContent.headerColor || '#d97706',
				accentColor: menuContent.accentColor || '#d97706',
			};
		} else if (experienceType === 'pdf') {
			liveContent = {
				type: 'pdf',
				directPdf: pdfContent.directPdf || false,
				title: pdfContent.title || 'Product Catalogue 2026',
				description:
					pdfContent.description ||
					'Explore our full collection of products, technical specifications, and seasonal pricing.',
				company: pdfContent.company || 'Acme Solutions Inc.',
				pdfUrl: pdfContent.pdfUrl || 'https://example.com/sample.pdf',
				fileSize: pdfContent.fileSize || '3.4 MB',
				pageCount: pdfContent.pageCount || '16 pages',
				coverUrl: pdfContent.coverUrl,
				website: pdfContent.website,
				email: pdfContent.email,
				phone: pdfContent.phone,
				headerColor: pdfContent.headerColor || '#0f172a',
				accentColor: pdfContent.accentColor || '#0f172a',
			};
		} else if (experienceType === 'coupon') {
			liveContent = {
				type: 'coupon',
				company: couponContent.company || 'Bloom & Wild Boutique',
				title: couponContent.title || '25% Off Your Next Order',
				couponCode: couponContent.couponCode || 'SAVE25',
				discount: couponContent.discount || '25% OFF',
				validUntil: couponContent.validUntil || 'Valid through Dec 31, 2026',
				terms:
					couponContent.terms ||
					'Valid on in-store and online purchases over $40. Limit one per customer.',
				website: couponContent.website,
				phone: couponContent.phone,
				address: couponContent.address || '789 Broadway Ave, New York, NY',
				headerColor: couponContent.headerColor || '#059669',
				accentColor: couponContent.accentColor || '#059669',
			};
		} else if (experienceType === 'app') {
			liveContent = {
				type: 'app',
				appName: appContent.appName || 'Ekutu QR Mobile',
				developer: appContent.developer || 'Ekutu Limited',
				description:
					appContent.description ||
					'Download our official mobile app for the fastest scanning and QR code creation on iOS and Android.',
				logoUrl: appContent.logoUrl,
				iosUrl: appContent.iosUrl || 'https://apps.apple.com',
				androidUrl: appContent.androidUrl || 'https://play.google.com',
				websiteUrl: appContent.websiteUrl,
				headerColor: appContent.headerColor || '#2563eb',
				accentColor: appContent.accentColor || '#2563eb',
			};
		} else {
			// Business Card
			liveContent = {
				type: 'business-card',
				fullName: hasUserBusinessData ? (businessContent.fullName ?? '') : 'Jane Doe',
				company: hasUserBusinessData ? (businessContent.company ?? '') : 'Acme Corporation',
				role: hasUserBusinessData ? (businessContent.role ?? '') : 'Managing Director',
				tagline: hasUserBusinessData
					? (businessContent.tagline ?? '')
					: 'Design & Technology Studio',
				logoUrl: businessContent.logoUrl,
				phone: hasUserBusinessData ? businessContent.phone : '+1 (555) 234-5678',
				email: hasUserBusinessData ? businessContent.email : 'hello@example.com',
				whatsApp: hasUserBusinessData ? businessContent.whatsApp : '+1 (555) 234-5678',
				website: businessContent.website,
				address: businessContent.address?.trim() || undefined,
				mapUrl: businessContent.address?.trim()
					? `https://maps.google.com/?q=${encodeURIComponent(businessContent.address.trim())}`
					: undefined,
				bio: businessContent.bio,
				about: hasUserBusinessData ? businessContent.about || businessContent.bio : undefined,
				openingHours: businessContent.openingHours,
				facilities: businessContent.facilities ?? [
					'wifi',
					'parking',
					'meeting-room',
					'refreshments',
				],
				socialLinks:
					businessContent.socialLinks && businessContent.socialLinks.length > 0
						? businessContent.socialLinks
						: hasUserBusinessData
							? undefined
							: [
									{ platform: 'linkedin', url: 'https://linkedin.com' },
									{ platform: 'instagram', url: 'https://instagram.com' },
								],
				headerColor: businessContent.headerColor || '#1d4ed8',
				accentColor: businessContent.accentColor || '#1d4ed8',
			};
		}

		liveExperience = {
			publicSlug: 'preview-slug',
			content: liveContent,
		};
	}

	return (
		<div className="flex flex-col items-center select-none">
			{/* Precise 220px × 435px container to guarantee 100% viewport fit on laptops */}
			<div className="w-[220px] h-[435px] relative flex justify-center items-start overflow-visible">
				<div className="scale-[0.74] origin-top">
					{/* Slender iPhone Frame (290px × 580px) -> Rendered at exactly 215px × 429px */}
					<div className="relative w-[290px] h-[580px] select-none mx-auto">
						{/* Physical side buttons */}
						<div className="absolute -left-[3px] top-[75px] w-[3px] h-[18px] bg-[#3a3447] rounded-l-[2px] shadow-xs" />
						<div className="absolute -left-[3px] top-[102px] w-[3px] h-[32px] bg-[#3a3447] rounded-l-[2px] shadow-xs" />
						<div className="absolute -left-[3px] top-[142px] w-[3px] h-[32px] bg-[#3a3447] rounded-l-[2px] shadow-xs" />
						<div className="absolute -right-[3px] top-[110px] w-[3px] h-[46px] bg-[#3a3447] rounded-r-[2px] shadow-xs" />

						{/* Outer Titanium Chassis */}
						<div className="relative w-full h-full rounded-[44px] p-[5px] bg-gradient-to-b from-[#3a3447] via-[#1a1722] to-[#3a3447] shadow-[0_24px_50px_-12px_rgba(15,10,25,0.38),0_0_0_1px_rgba(255,255,255,0.1)_inset] border border-[#484058]">
							{/* Inner Black Bezel */}
							<div className="relative w-full h-full rounded-[39px] bg-black p-[2px] overflow-hidden flex flex-col shadow-inner">
								{/* Status Bar & Dynamic Island */}
								<div className="absolute top-0 left-0 right-0 h-6 px-4 pt-1 flex items-center justify-between text-white text-[9.5px] font-semibold z-30 pointer-events-none">
									<span className="tracking-tight ml-0.5">9:41</span>

									{/* Dynamic Island Pill */}
									<div className="absolute left-1/2 -translate-x-1/2 top-1.5 h-[16px] w-[70px] bg-black rounded-full flex items-center justify-between px-2 shadow-sm">
										<span className="size-1.5 rounded-full bg-slate-900 border border-slate-800" />
										<span className="size-1.5 rounded-full bg-blue-950/80 border border-blue-900/50" />
									</div>

									<div className="flex items-center gap-1 opacity-90 mr-0.5">
										<svg className="size-2.5 fill-current" viewBox="0 0 24 24">
											<path d="M2 17h3v4H2v-4zm6-5h3v9H8v-9zm6-5h3v14h-3V7zm6-5h3v19h-3V2z" />
										</svg>
										<div className="w-3.5 h-2 rounded-2xs border border-white p-0.5 flex items-center">
											<div className="h-full w-full bg-white rounded-3xs" />
										</div>
									</div>
								</div>

								{/* Scrollable Viewport */}
								<div
									tabIndex={0}
									aria-label="Mobile preview screen (scrollable)"
									className="relative flex-1 w-full bg-slate-50 rounded-[37px] overflow-y-auto overflow-x-hidden scroll-smooth overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden focus:outline-none"
								>
									{experienceType === 'vcard' ? (
										<VCardPhoneScreen vcard={vcardData} />
									) : experienceType === 'website' ? (
										<WebsitePhoneScreen url={websiteUrl} />
									) : experienceType === 'whatsapp' ? (
										<WhatsAppPhoneScreen data={whatsappData} />
									) : experienceType === 'wifi' ? (
										<WifiPhoneScreen data={wifiData} />
									) : experienceType === 'event' ? (
										<EventPhoneScreen data={eventData} />
									) : liveExperience ? (
										<PublicExperiencePage experience={liveExperience} previewMode={true} />
									) : (
										<WebsitePhoneScreen url={websiteUrl} />
									)}
								</div>

								{/* Bottom Home Indicator */}
								<div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-20 h-1 bg-black/70 rounded-full z-30 pointer-events-none" />

								{/* Bottom Right Floating 3-Dots Action Pill */}
								<div
									className="absolute bottom-3 right-2.5 size-6 bg-[#4f75e3] hover:bg-[#3d62cf] rounded-md shadow-md flex items-center justify-center text-white z-20 cursor-pointer transition"
									title="Action Menu"
								>
									<svg className="size-2.5 fill-current" viewBox="0 0 24 24">
										<circle cx="12" cy="5" r="2" />
										<circle cx="12" cy="12" r="2" />
										<circle cx="12" cy="19" r="2" />
									</svg>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Scroll indicator guidance */}
			<p className="mt-1 text-center text-[10px] font-medium text-slate-500 flex items-center gap-1">
				<span>↕</span> Scroll inside phone to explore
			</p>
		</div>
	);
}
